import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Label } from "~/components/ui/label";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { isWorkEmail } from "~/modules/user/util/isConsumerEmail";

// // // //

interface Props {
    disabled: boolean;
    variant?: "login" | "register";
    onSubmit: (email: string) => void;
    showLoading?: boolean;
    isWorkEmailOnly?: boolean;
}

const variantToSubmitLabel: Record<"login" | "register", string> = {
    login: "Log in",
    register: "Create account",
};

/**
 * Collects an email address for authentication flows, with optional work-email validation.
 */
export function EmailForm(props: Props) {
    const {
        disabled,
        onSubmit,
        showLoading = false,
        isWorkEmailOnly = false,
        variant = "register",
    } = props;
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (isWorkEmailOnly && !isWorkEmail(email)) {
            setEmailError("Please use your work email address");
            return;
        }

        setEmailError("");
        onSubmit(email);
    };

    const handleEmailChange = (value: string) => {
        setEmail(value);
        if (emailError) {
            setEmailError("");
        }
    };

    return (
        <form
            className="flex flex-col gap-4 sm:gap-6 text-left"
            onSubmit={handleSubmit}
        >
            <div>
                <Label
                    htmlFor="email"
                    className="block text-xs font-semibold text-secondary-foreground sm:text-sm mb-1"
                >
                    {isWorkEmailOnly ? "Work email address" : "Email"}
                </Label>
                <Input
                    id="email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    disabled={disabled}
                    value={email}
                    placeholder={
                        isWorkEmailOnly
                            ? "What's your work email?"
                            : "you@company.com"
                    }
                    onChange={(e) => handleEmailChange(e.target.value)}
                    aria-invalid={emailError ? true : undefined}
                    aria-describedby={emailError ? "email-error" : undefined}
                />
                {emailError ? (
                    <p
                        id="email-error"
                        className="mt-2 text-sm text-red-600"
                        role="alert"
                    >
                        {emailError}
                    </p>
                ) : null}
            </div>

            <Button
                type="submit"
                disabled={disabled}
                variant="primary"
                className="font-normal"
            >
                {showLoading && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                )}
                <span>{variantToSubmitLabel[variant]}</span>
            </Button>
        </form>
    );
}
